import React from 'react';
import { AppProvider, useAppContext } from './context/AppContext';
import { PlanProvider, usePlan } from './context/PlanContext';
import { defaultUser, defaultBonusTiers } from './utils/commissionCalc';
import Dashboard from './components/Dashboard';
import Earnings from './components/Earnings';
import Memos from './components/Memos';
import Leads from './components/Leads';
import MyProfile from './components/MyProfile';
import PlansPage from './components/PlansPage';
import AIChatbot, { AIFab } from './components/AIChatbot';
import AddSaleModal from './components/AddSaleModal';
import QuickTouchModal from './components/QuickTouchModal';
import CelebrationModal from './components/CelebrationModal';
import BottomNav from './components/BottomNav';
import './styles/global.css';

function AppShell() {
  const { activeTab, setActiveTab, userData, theme, showAI } = useAppContext();
  const { planKey } = usePlan();

  // Plan badge style
  const badgeStyle = {
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 800,
    padding: '2px 8px',
    borderRadius: 20,
    marginLeft: 6,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    cursor: 'pointer',
    background: planKey === 'pro'
      ? 'linear-gradient(135deg,#3b82f6,#2dd4bf)'
      : 'rgba(255,255,255,.15)',
    color: planKey === 'pro' ? '#fff' : 'rgba(255,255,255,.6)',
    border: planKey === 'pro' ? 'none' : '1px solid rgba(255,255,255,.2)',
  };

  return (
    <div className="app" style={{
      '--theme-bg': theme.bg,
      '--theme-primary': theme.primary,
      '--theme-secondary': theme.secondary,
      '--theme-accent': theme.accent,
      '--theme-hero-gradient': theme.heroGradient,
      '--theme-card-bg': theme.cardBg
    }}>
      <header className="topbar">
        <div className="logo">TRACKSY</div>
        <div className="topbar-right" onClick={() => setActiveTab('profile')} style={{cursor:'pointer'}}>
          <div className="topbar-avatar">{(userData.name||'?')[0].toUpperCase()}</div>
          <div className="topbar-user">
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <span className="topbar-name">{userData.name || 'Your Name'}</span>
              <span style={badgeStyle} onClick={(e) => { e.stopPropagation(); setActiveTab('plans'); }}>
                {planKey === 'pro' ? 'Pro' : 'Core'}
              </span>
            </div>
            {userData.role && <span className="topbar-role">{userData.role}</span>}
          </div>
        </div>
      </header>

      <main>
        {activeTab === 'dashboard'  && <Dashboard />}
        {activeTab === 'commission' && <Earnings />}
        {activeTab === 'leads'      && <Leads />}
        {activeTab === 'memos'      && <Memos />}
        {activeTab === 'profile'    && <MyProfile />}
        {activeTab === 'plans'      && <PlansPage />}
      </main>

      <BottomNav />
      <AIFab />
      <AddSaleModal />
      <QuickTouchModal />
      <CelebrationModal />
      {showAI && <AIChatbot />}
    </div>
  );
}

export default function TracksyApp({
  initialUserData = defaultUser,
  initialSales = [],
  initialActivities = [],
  initialBonusTiers = defaultBonusTiers,
  initialVoiceMemos = [],
  initialLeads = [],
  initialAppointments = [],
  onDataChange = () => {},
  onLogout = () => {},
  userEmail = '',
  userUid = '',
}) {
  return (
    <PlanProvider uid={userUid} email={userEmail}>
      <AppProvider
        initialUserData={initialUserData}
        initialSales={initialSales}
        initialActivities={initialActivities}
        initialBonusTiers={initialBonusTiers}
        initialVoiceMemos={initialVoiceMemos}
        initialLeads={initialLeads}
        initialAppointments={initialAppointments}
        onDataChange={onDataChange}
        onLogout={onLogout}
        userEmail={userEmail}
        userUid={userUid}
      >
        <AppShell />
      </AppProvider>
    </PlanProvider>
  );
}
